import { getTemplateSeoTaxonomy } from './templateSeo.js';

const REFERENCE_PATHS = {
  ultrassonografia: '/referencias-laudos/como-fazer-laudo-ultrassonografia',
  'raio-x': '/referencias-laudos/como-fazer-laudo-radiografia',
  ecg: '/referencias-laudos/como-laudar-ecg',
  tomografia: '/referencias-laudos/estrutura-laudo-tomografia',
  ressonancia: '/referencias-laudos/estrutura-laudo-ressonancia',
};

const MODALITY_PROFILE = {
  ultrassonografia: {
    label: 'ultrassonografia',
    structure: ['Técnica e escopo do exame', 'Descrição em ordem anatômica', 'Medidas e observações aplicáveis', 'Conclusão'],
    checklist: ['Confirmar região e tipo de exame', 'Revisar medidas e unidades', 'Remover órgãos ou estruturas não avaliadas', 'Conferir coerência entre descrição e conclusão'],
    adaptation: 'Adapte a sequência anatômica, medidas e descrições ao exame efetivamente realizado. Remova campos que não pertençam ao estudo e confira unidades antes de emitir.',
  },
  'raio-x': {
    label: 'radiografia',
    structure: ['Técnica e incidências quando pertinentes', 'Descrição sistemática da região', 'Comparação quando aplicável', 'Conclusão'],
    checklist: ['Conferir região e lateralidade', 'Revisar incidências e limitações descritas', 'Remover frases residuais de outra região', 'Confirmar que a conclusão deriva dos achados descritos'],
    adaptation: 'Mantenha somente incidências, regiões e achados realmente avaliados. Revise lateralidade, comparação e qualquer texto herdado de outro exame.',
  },
  ecg: {
    label: 'ECG',
    structure: ['Frequência e ritmo', 'Intervalos e condução', 'Eixo e morfologia', 'Conclusão eletrocardiográfica'],
    checklist: ['Conferir frequência e ritmo descritos', 'Revisar intervalos e eixo informados', 'Remover valores residuais de outro traçado', 'Confirmar que a conclusão corresponde à análise profissional'],
    adaptation: 'Use a estrutura apenas para organizar a documentação. Frequência, ritmo, intervalos, eixo, condução e conclusão devem refletir a análise do traçado pelo profissional.',
  },
  tomografia: {
    label: 'tomografia',
    structure: ['Técnica e protocolo', 'Descrição por região', 'Comparação quando aplicável', 'Conclusão'],
    checklist: ['Conferir protocolo e região examinada', 'Revisar localização e medidas', 'Checar referências a exames anteriores', 'Confirmar correspondência entre corpo e conclusão'],
    adaptation: 'Adapte técnica, sequência anatômica, medidas e comparação ao protocolo realizado. A conclusão deve sintetizar somente o que foi documentado.',
  },
  ressonancia: {
    label: 'ressonância magnética',
    structure: ['Técnica e protocolo quando pertinentes', 'Descrição anatômica organizada', 'Níveis, lados e medidas quando aplicáveis', 'Conclusão'],
    checklist: ['Conferir região e protocolo', 'Revisar níveis e lateralidade', 'Checar medidas e terminologia', 'Confirmar coerência entre achados e conclusão'],
    adaptation: 'Revise protocolo, anatomia, níveis, lados e medidas para que o documento represente o exame analisado. Remova seções não aplicáveis.',
  },
  outros: {
    label: 'laudo',
    structure: ['Técnica ou contexto aplicável', 'Descrição dos achados', 'Observações pertinentes', 'Conclusão'],
    checklist: ['Confirmar exame e contexto', 'Remover conteúdo não aplicável', 'Revisar medidas e lateralidade quando presentes', 'Confirmar coerência da conclusão'],
    adaptation: 'Use o modelo como ponto de partida de estrutura. Mantenha somente informações sustentadas pelo exame e revise o documento completo antes da emissão.',
  },
};

const cleanDisplayName = (template = {}) => String(template?.name || 'laudo')
  .replace(/^modelo\s+de\s+laudo\s+de\s+/i, '')
  .replace(/^modelo\s+de\s+/i, '')
  .trim();

const isNormalIntent = (template = {}) => /\bnormal\b/i.test(`${template?.name || ''} ${template?.seo_slug || ''}`);

export const getReferencePathForModality = (modality) => REFERENCE_PATHS[modality] || null;

export const buildModelSeoProfile = (template = {}) => {
  const taxonomy = getTemplateSeoTaxonomy(template);
  const isVeterinary = taxonomy.modality === 'veterinaria' || template?.category === 'veterinario';
  const modalityProfile = MODALITY_PROFILE[taxonomy.modality] || MODALITY_PROFILE.outros;
  const subject = cleanDisplayName(template);
  const normal = isNormalIntent(template);
  const h1 = normal ? `Modelo de laudo de ${subject}` : `Modelo de laudo de ${subject}`;
  const title = normal
    ? `${subject}: modelo de laudo e exemplo | Laudando`
    : `Modelo de laudo de ${subject} | Exemplo editável | Laudando`;
  const description = template?.seo_description || template?.description || `Veja um modelo de laudo de ${subject}, entenda a estrutura e abra o documento editável no Laudando para adaptar ao exame realizado.`;

  return {
    h1,
    title,
    description,
    direct_answer: `Este modelo de ${subject.toLowerCase()} oferece uma estrutura editável para uso profissional. Revise técnica, descrição e conclusão de acordo com o exame efetivamente realizado antes de emitir.`,
    structure_labels: modalityProfile.structure,
    review_checklist: modalityProfile.checklist,
    adaptation_guidance: modalityProfile.adaptation,
    reference_path: isVeterinary ? null : getReferencePathForModality(taxonomy.modality),
    modality: taxonomy.modality,
    modality_label: taxonomy.modalityLabel,
    is_veterinary: isVeterinary,
  };
};

export const rankRelatedTemplates = (current, allTemplates = [], limit = 6) => {
  const currentTaxonomy = getTemplateSeoTaxonomy(current);
  const currentPath = currentTaxonomy.path;
  return (allTemplates || [])
    .filter((item) => item?.id !== current?.id)
    .map((item) => {
      const taxonomy = getTemplateSeoTaxonomy(item);
      let score = 0;
      if (taxonomy.modality === currentTaxonomy.modality) score += 10;
      const currentParts = currentPath.split('/').filter(Boolean);
      const itemParts = taxonomy.path.split('/').filter(Boolean);
      score += currentParts.filter((part) => itemParts.includes(part)).length;
      score += Math.min(Number(item?.usage_count || 0) / 1000, 2);
      return { item, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ item }) => item);
};

export const getHubSeoProfile = (modality) => {
  if (modality === 'veterinaria') return {
    reference_path: null,
    intro: 'Modelos veterinários permanecem em um silo próprio, voltado a médicos-veterinários e separado da arquitetura médica humana.',
  };
  const profile = MODALITY_PROFILE[modality] || MODALITY_PROFILE.outros;
  return {
    reference_path: getReferencePathForModality(modality),
    intro: `Consulte modelos profissionais de ${profile.label}, compare estruturas e abra o documento escolhido diretamente no editor. Cada modelo é um ponto de partida que deve ser adaptado ao exame realizado.`,
  };
};
